/**
 * Revive Tweak — Photoshop-style silhouette editor.
 * White outlines on every item. Drag a corner to scale, drag a point to
 * reshape, drag a handle to curve, click the stroke to add a corner.
 */

const HIT_PT = 12;
const HIT_HANDLE = 10;
const CLICK_PX = 5;

function lerp(a, b, t) {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function cubicAt(p0, p1, p2, p3, t) {
  const u = 1 - t;
  const uu = u * u;
  const tt = t * t;
  return {
    x: uu * u * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + tt * t * p3.x,
    y: uu * u * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + tt * t * p3.y,
  };
}

function splitCubic(p0, p1, p2, p3, t) {
  const p01 = lerp(p0, p1, t);
  const p12 = lerp(p1, p2, t);
  const p23 = lerp(p2, p3, t);
  const p012 = lerp(p01, p12, t);
  const p123 = lerp(p12, p23, t);
  const mid = lerp(p012, p123, t);
  return { left: [p0, p01, p012, mid], right: [mid, p123, p23, p3] };
}

export function rectToPoly(rect) {
  const { x, y, w, h } = rect;
  return [
    [x, y],
    [x + w, y],
    [x + w, y + h],
    [x, y + h],
  ];
}

export function polyToRing(poly) {
  const n = poly.length;
  if (n < 3) return [];
  const pts = poly.map(([x, y]) => ({
    x,
    y,
    ix: x,
    iy: y,
    ox: x,
    oy: y,
    smooth: true,
  }));
  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n];
    const next = pts[(i + 1) % n];
    const p = pts[i];
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const len = Math.hypot(dx, dy) || 1;
    const dPrev = Math.hypot(p.x - prev.x, p.y - prev.y);
    const dNext = Math.hypot(next.x - p.x, next.y - p.y);
    const tIn = dPrev * 0.32;
    const tOut = dNext * 0.32;
    p.ix = p.x - (dx / len) * tIn;
    p.iy = p.y - (dy / len) * tIn;
    p.ox = p.x + (dx / len) * tOut;
    p.oy = p.y + (dy / len) * tOut;
  }
  return pts;
}

export function ringToD(pts) {
  if (pts.length < 2) return "";
  let d = `M${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    d += `C${a.ox} ${a.oy} ${b.ix} ${b.iy} ${b.x} ${b.y}`;
  }
  return `${d}Z`;
}

function sampleRing(pts, steps = 8) {
  const out = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const p0 = { x: a.x, y: a.y };
    const p1 = { x: a.ox, y: a.oy };
    const p2 = { x: b.ix, y: b.iy };
    const p3 = { x: b.x, y: b.y };
    for (let s = 0; s < steps; s++) out.push([cubicAt(p0, p1, p2, p3, s / steps).x, cubicAt(p0, p1, p2, p3, s / steps).y]);
  }
  return out;
}

function ringBounds(rings) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const ring of rings) {
    for (const p of ring) {
      minX = Math.min(minX, p.x, p.ix, p.ox);
      minY = Math.min(minY, p.y, p.iy, p.oy);
      maxX = Math.max(maxX, p.x, p.ix, p.ox);
      maxY = Math.max(maxY, p.y, p.iy, p.oy);
    }
  }
  if (!Number.isFinite(minX)) return { x: 0, y: 0, w: 0, h: 0 };
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function scaleRings(rings, cx, cy, sx, sy) {
  for (const ring of rings) {
    for (const p of ring) {
      p.x = cx + (p.x - cx) * sx;
      p.y = cy + (p.y - cy) * sy;
      p.ix = cx + (p.ix - cx) * sx;
      p.iy = cy + (p.iy - cy) * sy;
      p.ox = cx + (p.ox - cx) * sx;
      p.oy = cy + (p.oy - cy) * sy;
    }
  }
}

function translateRings(rings, dx, dy) {
  for (const ring of rings) {
    for (const p of ring) {
      p.x += dx;
      p.y += dy;
      p.ix += dx;
      p.iy += dy;
      p.ox += dx;
      p.oy += dy;
    }
  }
}

function closestOnRing(ring, x, y) {
  let best = { dist: Infinity, i: 0, t: 0, pt: { x, y } };
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    const p0 = { x: a.x, y: a.y };
    const p1 = { x: a.ox, y: a.oy };
    const p2 = { x: b.ix, y: b.iy };
    const p3 = { x: b.x, y: b.y };
    for (let s = 0; s <= 24; s++) {
      const t = s / 24;
      const pt = cubicAt(p0, p1, p2, p3, t);
      const d = Math.hypot(pt.x - x, pt.y - y);
      if (d < best.dist) best = { dist: d, i, t, pt };
    }
  }
  return best;
}

function insertOnRing(ring, seg, t) {
  const a = ring[seg];
  const b = ring[(seg + 1) % ring.length];
  const split = splitCubic(
    { x: a.x, y: a.y },
    { x: a.ox, y: a.oy },
    { x: b.ix, y: b.iy },
    { x: b.x, y: b.y },
    t,
  );
  a.ox = split.left[1].x;
  a.oy = split.left[1].y;
  b.ix = split.right[2].x;
  b.iy = split.right[2].y;
  const mid = {
    x: split.left[3].x,
    y: split.left[3].y,
    ix: split.left[2].x,
    iy: split.left[2].y,
    ox: split.right[1].x,
    oy: split.right[1].y,
    smooth: true,
  };
  ring.splice(seg + 1, 0, mid);
  return seg + 1;
}

export class ReviveOutlineEditor {
  /**
   * @param {{
   *   root: HTMLElement,
   *   svg: SVGSVGElement,
   *   items: { hb: object, path: SVGPathElement, rings: object[][] }[],
   *   onDrag?: (on: boolean) => void,
   *   onRemove?: (hb: object) => void,
   *   onSelectionChange?: (on: boolean) => void,
   * }} opts
   */
  constructor(opts) {
    this.root = opts.root;
    this.svg = opts.svg;
    this.items = opts.items;
    this.onDrag = opts.onDrag ?? (() => {});
    this.onRemove = opts.onRemove ?? (() => {});
    this.onSelectionChange = opts.onSelectionChange ?? (() => {});
    this.enabled = false;
    this.dragging = false;
    this._sel = null;
    this._pt = -1;
    this._chrome = null;
    this._drag = null;
    this._onDown = (e) => this._pointerDown(e);
    this._onMove = (e) => this._pointerMove(e);
    this._onUp = (e) => this._pointerUp(e);
    this._onKey = (e) => this._key(e);
    this._chrome = document.createElement("div");
    this._chrome.className = "revive-tweak-chrome";
    this._chrome.setAttribute("aria-hidden", "true");
    this.root.appendChild(this._chrome);
  }

  setEnabled(on) {
    this.enabled = Boolean(on);
    this.root?.classList.toggle("revive-layer--tweak", this.enabled);
    if (!this.enabled) this.deselect();
    this._paint();
  }

  hasSelection() {
    return this._sel != null;
  }

  deselect() {
    const had = this._sel != null;
    this._sel = null;
    this._pt = -1;
    this._paint();
    if (had) this.onSelectionChange(false);
  }

  /** Drop the selected item outline so it no longer takes hover or a price card. */
  removeSelected() {
    if (!this.enabled || this._sel == null) return false;
    const it = this.items[this._sel];
    if (!it) return false;
    it.path.removeEventListener("pointerdown", this._onDown);
    it.path.remove();
    this.items.splice(this._sel, 1);
    this._sel = null;
    this._pt = -1;
    this._drag = null;
    this.dragging = false;
    this.onDrag(false);
    this._paint();
    this.onSelectionChange(false);
    this.onRemove(it.hb);
    return true;
  }

  destroy() {
    this.setEnabled(false);
    window.removeEventListener("pointermove", this._onMove);
    window.removeEventListener("pointerup", this._onUp);
    window.removeEventListener("keydown", this._onKey);
    if (this._onBlank && this.root) this.root.removeEventListener("pointerdown", this._onBlank);
    this._chrome?.remove();
    this._chrome = null;
    for (const it of this.items) {
      it.path.removeEventListener("pointerdown", this._onDown);
    }
  }

  bind() {
    for (const it of this.items) {
      it.path.addEventListener("pointerdown", this._onDown);
    }
    this._onBlank = (e) => {
      if (!this.enabled) return;
      if (e.target === this.root || e.target.classList?.contains("revive-layer__img")) {
        this.deselect();
      }
    };
    this.root.addEventListener("pointerdown", this._onBlank);
    window.addEventListener("keydown", this._onKey);
  }

  _norm(e) {
    const r = this.svg.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) / Math.max(1, r.width),
      y: (e.clientY - r.top) / Math.max(1, r.height),
      px: Math.max(1, r.width),
      py: Math.max(1, r.height),
    };
  }

  _persist(it) {
    it.hb.outline = it.rings.map((ring) => sampleRing(ring, 7));
    it.path.setAttribute("d", it.rings.map(ringToD).join(""));
  }

  _select(index, pt = -1) {
    const changed = this._sel !== index;
    this._sel = index;
    this._pt = pt;
    this._paint();
    if (changed) this.onSelectionChange(true);
  }

  _pointerDown(e) {
    if (!this.enabled || e.button !== 0) return;
    if (e.target.closest?.(".revive-layer__enter, .revive-layer__photoreal, .revive-layer__gallery, .revive-layer__tweak, .revive-layer__remove")) {
      return;
    }
    const n = this._norm(e);
    const hit = this._hitWidget(e) ?? this._hitPath(n, e);
    if (!hit) {
      this.deselect();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    this._select(hit.item, hit.pt ?? this._pt);
    this.dragging = true;
    this.onDrag(true);
    const it = this.items[hit.item];
    const box = ringBounds(it.rings);
    const ring = it.rings[hit.ring ?? 0];
    const p = hit.pt >= 0 ? ring?.[hit.pt] : null;
    this._drag = {
      kind: hit.kind,
      item: hit.item,
      ring: hit.ring ?? 0,
      pt: hit.pt ?? -1,
      handle: hit.handle ?? null,
      start: { x: n.x, y: n.y, clientX: e.clientX, clientY: e.clientY },
      box,
      snapshot: structuredClone(it.rings),
      origin: p ? { x: p.x, y: p.y, ix: p.ix, iy: p.iy, ox: p.ox, oy: p.oy } : null,
      moved: false,
      addOnClick: hit.kind === "path",
      addAt: hit.addAt ?? null,
    };
    window.addEventListener("pointermove", this._onMove);
    window.addEventListener("pointerup", this._onUp);
  }

  _pointerMove(e) {
    if (!this._drag) return;
    const n = this._norm(e);
    const d = this._drag;
    const dist = Math.hypot(e.clientX - d.start.clientX, e.clientY - d.start.clientY);
    if (dist > CLICK_PX) d.moved = true;
    const it = this.items[d.item];
    if (d.kind === "scale") {
      const cx = d.box.x + d.box.w / 2;
      const cy = d.box.y + d.box.h / 2;
      const s0 = Math.hypot(d.start.x - cx, d.start.y - cy) || 1e-6;
      const s1 = Math.hypot(n.x - cx, n.y - cy);
      const s = Math.min(6, Math.max(0.12, s1 / s0));
      it.rings = structuredClone(d.snapshot);
      scaleRings(it.rings, cx, cy, s, s);
    } else if (d.kind === "point") {
      const ring = it.rings[d.ring];
      const p = ring[d.pt];
      const dx = n.x - d.start.x;
      const dy = n.y - d.start.y;
      p.x = d.origin.x + dx;
      p.y = d.origin.y + dy;
      p.ix = d.origin.ix + dx;
      p.iy = d.origin.iy + dy;
      p.ox = d.origin.ox + dx;
      p.oy = d.origin.oy + dy;
    } else if (d.kind === "handle") {
      const p = it.rings[d.ring][d.pt];
      const keepIn = Math.hypot(d.origin.x - d.origin.ix, d.origin.y - d.origin.iy);
      const keepOut = Math.hypot(d.origin.ox - d.origin.x, d.origin.oy - d.origin.y);
      if (d.handle === "out") {
        p.ox = n.x;
        p.oy = n.y;
        if (p.smooth && !e.altKey) {
          const vx = p.x - p.ox;
          const vy = p.y - p.oy;
          const len = Math.hypot(vx, vy) || 1;
          p.ix = p.x + (vx / len) * keepIn;
          p.iy = p.y + (vy / len) * keepIn;
        } else {
          p.smooth = false;
        }
      } else {
        p.ix = n.x;
        p.iy = n.y;
        if (p.smooth && !e.altKey) {
          const vx = p.x - p.ix;
          const vy = p.y - p.iy;
          const len = Math.hypot(vx, vy) || 1;
          p.ox = p.x + (vx / len) * keepOut;
          p.oy = p.y + (vy / len) * keepOut;
        } else {
          p.smooth = false;
        }
      }
    } else if (d.kind === "path" && d.moved) {
      d.addOnClick = false;
      it.rings = structuredClone(d.snapshot);
      translateRings(it.rings, n.x - d.start.x, n.y - d.start.y);
    }
    this._persist(it);
    this._paint();
  }

  _pointerUp() {
    const d = this._drag;
    window.removeEventListener("pointermove", this._onMove);
    window.removeEventListener("pointerup", this._onUp);
    this.dragging = false;
    this.onDrag(false);
    if (d?.addOnClick && !d.moved && d.addAt) {
      const it = this.items[d.item];
      const ring = it.rings[d.addAt.ring];
      const idx = insertOnRing(ring, d.addAt.i, d.addAt.t);
      this._pt = idx;
      this._persist(it);
      this._paint();
    }
    this._drag = null;
  }

  _key(e) {
    if (!this.enabled || this._sel == null) return;
    if (document.body.classList.contains("revive-gallery-open")) return;
    const tag = e.target?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
    if (e.key === "Delete" || e.key === "Del") {
      e.preventDefault();
      this.removeSelected();
      return;
    }
    if (e.key !== "Backspace") return;
    const it = this.items[this._sel];
    const ring = it?.rings?.[0];
    if (!ring || this._pt < 0 || ring.length <= 3) {
      e.preventDefault();
      this.removeSelected();
      return;
    }
    e.preventDefault();
    ring.splice(this._pt, 1);
    this._pt = Math.min(this._pt, ring.length - 1);
    this._persist(it);
    this._paint();
  }

  _hitWidget(e) {
    const node = e.target.closest?.("[data-tweak]");
    if (!node) return null;
    const kind = node.dataset.tweak;
    const item = Number(node.dataset.item);
    if (kind === "scale") return { kind, item, corner: node.dataset.corner };
    if (kind === "point") return { kind, item, ring: Number(node.dataset.ring), pt: Number(node.dataset.pt) };
    if (kind === "handle") {
      return { kind, item, ring: Number(node.dataset.ring), pt: Number(node.dataset.pt), handle: node.dataset.handle };
    }
    return null;
  }

  _hitPath(n, e) {
    const r = this.svg.getBoundingClientRect();
    const thresh = HIT_PT / Math.min(r.width, r.height);
    for (let i = this.items.length - 1; i >= 0; i--) {
      const it = this.items[i];
      if (!it.path.contains(e.target) && e.target !== it.path) continue;
      for (let ri = 0; ri < it.rings.length; ri++) {
        const ring = it.rings[ri];
        for (let pi = 0; pi < ring.length; pi++) {
          const p = ring[pi];
          if (Math.hypot((p.x - n.x) * r.width, (p.y - n.y) * r.height) <= HIT_PT) {
            return { kind: "point", item: i, ring: ri, pt: pi };
          }
        }
        const near = closestOnRing(ring, n.x, n.y);
        if (near.dist <= thresh * 1.8) {
          return { kind: "path", item: i, ring: ri, addAt: { ring: ri, i: near.i, t: near.t } };
        }
      }
      return { kind: "path", item: i, ring: 0, addAt: null };
    }
    return null;
  }

  _paint() {
    if (!this._chrome) return;
    this._chrome.innerHTML = "";
    this._chrome.hidden = !this.enabled;
    for (const it of this.items) {
      it.path.classList.toggle("revive-outline--tweak", this.enabled);
      it.path.classList.toggle("revive-outline--sel", this.enabled && this.items[this._sel] === it);
    }
    if (!this.enabled || this._sel == null) return;
    const it = this.items[this._sel];
    if (!it) return;
    const box = ringBounds(it.rings);
    const bbox = document.createElement("div");
    bbox.className = "revive-tweak-bbox";
    bbox.style.left = `${box.x * 100}%`;
    bbox.style.top = `${box.y * 100}%`;
    bbox.style.width = `${box.w * 100}%`;
    bbox.style.height = `${box.h * 100}%`;
    this._chrome.appendChild(bbox);

    const corners = [
      [box.x, box.y, "nw"],
      [box.x + box.w, box.y, "ne"],
      [box.x, box.y + box.h, "sw"],
      [box.x + box.w, box.y + box.h, "se"],
    ];
    for (const [x, y, corner] of corners) {
      const el = document.createElement("button");
      el.type = "button";
      el.className = `revive-tweak-scale revive-tweak-scale--${corner}`;
      el.dataset.tweak = "scale";
      el.dataset.item = String(this._sel);
      el.dataset.corner = corner;
      el.style.left = `${x * 100}%`;
      el.style.top = `${y * 100}%`;
      el.addEventListener("pointerdown", this._onDown);
      this._chrome.appendChild(el);
    }

    it.rings.forEach((ring, ri) => {
      ring.forEach((p, pi) => {
        if (pi === this._pt) {
          this._arm(p.x, p.y, p.ix, p.iy);
          this._arm(p.x, p.y, p.ox, p.oy);
          this._dot(p.ix, p.iy, "handle", "in", ri, pi);
          this._dot(p.ox, p.oy, "handle", "out", ri, pi);
        }
        const a = document.createElement("button");
        a.type = "button";
        a.className = "revive-tweak-anchor" + (pi === this._pt ? " revive-tweak-anchor--on" : "");
        a.dataset.tweak = "point";
        a.dataset.item = String(this._sel);
        a.dataset.ring = String(ri);
        a.dataset.pt = String(pi);
        a.style.left = `${p.x * 100}%`;
        a.style.top = `${p.y * 100}%`;
        a.addEventListener("pointerdown", this._onDown);
        this._chrome.appendChild(a);
      });
    });
  }

  _arm(x0, y0, x1, y1) {
    const el = document.createElement("div");
    el.className = "revive-tweak-arm";
    const ang = (Math.atan2(y1 - y0, x1 - x0) * 180) / Math.PI;
    el.style.left = `${x0 * 100}%`;
    el.style.top = `${y0 * 100}%`;
    el.style.transform = `rotate(${ang}deg)`;
    const r = this.root.getBoundingClientRect();
    el.style.width = `${Math.hypot((x1 - x0) * r.width, (y1 - y0) * r.height)}px`;
    this._chrome.appendChild(el);
  }

  _dot(x, y, kind, handle, ri, pi) {
    const el = document.createElement("button");
    el.type = "button";
    el.className = "revive-tweak-handle";
    el.dataset.tweak = kind;
    el.dataset.handle = handle;
    el.dataset.item = String(this._sel);
    el.dataset.ring = String(ri);
    el.dataset.pt = String(pi);
    el.style.left = `${x * 100}%`;
    el.style.top = `${y * 100}%`;
    el.addEventListener("pointerdown", this._onDown);
    this._chrome.appendChild(el);
  }
}

export default ReviveOutlineEditor;
